import * as React from 'react';

import type { VariantProps } from 'tailwind-variants';
import { tv } from 'tailwind-variants';

import type { IconProps } from '@avodah-inc/types';
import { cn } from '@avodah-inc/ui/utils';

const settingsVariants = tv({
  base: 'text-base-black',
  variants: {
    variant: {
      default: 'text-base-black',
      onDark: 'text-base-white',
      primary: 'text-primary',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

export type SettingsProps = React.SVGAttributes<SVGSVGElement> &
  VariantProps<typeof settingsVariants> &
  IconProps;

export const Settings = React.forwardRef<SVGSVGElement, SettingsProps>(
  (
    {
      className,
      variant,
      viewBox,
      width,
      height,
      fill = 'currentColor',
      ...props
    },
    ref,
  ) => {
    return (
      <svg
        className={cn(
          settingsVariants({
            variant,
            className,
          }),
        )}
        fill={fill}
        height={height}
        ref={ref}
        viewBox={viewBox}
        width={width}
        {...props}
      >
        <path d="M1.91101 7.38301C2.28021 6.24217 2.88685 5.19247 3.691 4.303C3.7575 4.22952 3.84434 4.17745 3.94048 4.15342C4.03663 4.12938 4.13775 4.13446 4.231 4.16801L6.14901 4.85401C6.28555 4.90276 6.43106 4.92125 6.57546 4.90817C6.71985 4.8951 6.85968 4.85079 6.98524 4.7783C7.11081 4.70581 7.21911 4.60689 7.30265 4.48839C7.38618 4.36988 7.44294 4.23463 7.46901 4.09201L7.83501 2.086C7.85268 1.98835 7.89902 1.89817 7.96811 1.82694C8.03721 1.75571 8.12594 1.70665 8.223 1.68601C9.39454 1.43653 10.6055 1.43653 11.777 1.68601C11.8741 1.70665 11.9628 1.75571 12.0319 1.82694C12.101 1.89817 12.1473 1.98835 12.165 2.086L12.531 4.09201C12.5571 4.23463 12.6138 4.36988 12.6974 4.48839C12.7809 4.60689 12.8892 4.70581 13.0148 4.7783C13.1403 4.85079 13.2802 4.8951 13.4246 4.90817C13.569 4.92125 13.7145 4.90276 13.851 4.85401L15.77 4.16801C15.8634 4.13458 15.9645 4.12966 16.0607 4.15387C16.1568 4.17809 16.2436 4.23035 16.31 4.304C17.114 5.19318 17.7207 6.24253 18.09 7.38301C18.1204 7.47731 18.122 7.5785 18.0948 7.67376C18.0676 7.76901 18.0126 7.85402 17.937 7.91801L16.382 9.23801C16.2715 9.33188 16.1828 9.44865 16.122 9.58023C16.0611 9.7118 16.0296 9.85504 16.0296 10C16.0296 10.145 16.0611 10.2882 16.122 10.4198C16.1828 10.5514 16.2715 10.6681 16.382 10.762L17.937 12.082C18.0125 12.1461 18.0672 12.2312 18.0943 12.3264C18.1213 12.4217 18.1195 12.5228 18.089 12.617C17.7198 13.7578 17.1132 14.8075 16.309 15.697C16.2425 15.7705 16.1557 15.8226 16.0595 15.8466C15.9634 15.8706 15.8623 15.8656 15.769 15.832L13.851 15.146C13.7145 15.0972 13.569 15.0788 13.4246 15.0918C13.2802 15.1049 13.1403 15.1492 13.0148 15.2217C12.8892 15.2942 12.7809 15.3931 12.6974 15.5116C12.6138 15.6301 12.5571 15.7654 12.531 15.908L12.165 17.915C12.1471 18.0125 12.1007 18.1024 12.0317 18.1735C11.9626 18.2445 11.8739 18.2934 11.777 18.314C10.6055 18.5636 9.39453 18.5636 8.223 18.314C8.12594 18.2934 8.03721 18.2443 7.96811 18.1731C7.89902 18.1018 7.85268 18.0117 7.83501 17.914L7.46901 15.908C7.44294 15.7654 7.38618 15.6301 7.30265 15.5116C7.21911 15.3931 7.11081 15.2942 6.98524 15.2217C6.85968 15.1492 6.71985 15.1049 6.57546 15.0918C6.43106 15.0788 6.28555 15.0972 6.14901 15.146L4.231 15.832C4.13765 15.8654 4.03647 15.8704 3.94032 15.8461C3.84417 15.8219 3.75738 15.7697 3.691 15.696C2.88695 14.8069 2.28031 13.7575 1.91101 12.617C1.88052 12.5228 1.87869 12.4217 1.90575 12.3264C1.93281 12.2312 1.98754 12.1461 2.063 12.082L3.61801 10.762C3.72847 10.6681 3.8172 10.5514 3.87806 10.4198C3.93891 10.2882 3.97043 10.145 3.97043 10C3.97043 9.85504 3.93891 9.7118 3.87806 9.58023C3.8172 9.44865 3.72847 9.33188 3.61801 9.23801L2.063 7.91801C1.98754 7.8539 1.93281 7.76883 1.90575 7.67359C1.87869 7.57834 1.88052 7.47721 1.91101 7.38301ZM8.00101 10C8.00101 10.5304 8.21172 11.0391 8.58679 11.4142C8.96186 11.7893 9.47057 12 10.001 12C10.5314 12 11.0401 11.7893 11.4152 11.4142C11.7903 11.0391 12.001 10.5304 12.001 10C12.001 9.46957 11.7903 8.96087 11.4152 8.58579C11.0401 8.21072 10.5314 8.00001 10.001 8.00001C9.47057 8.00001 8.96186 8.21072 8.58679 8.58579C8.21172 8.96087 8.00101 9.46957 8.00101 10Z" />
      </svg>
    );
  },
);

Settings.displayName = 'Settings';
